import dayjs from "dayjs";

import Sentry from "@similarweb/sentry-logger";

import type { ISortDirection } from "app/@types/userdata";
import type { ICampaignsScrapingConfiguration } from "app/components/campaigns/shared/CampaignsConst";
import { DefaultCellHeader } from "app/components/React/Table/headerCells";
import type { ISWReactTableWrapperWithServerDataProps } from "app/components/React/Table/SWReactTableWrapper";
import type { IRTCurrentCampaignData } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import { getCountryFromScrapingConfigId } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/KeywordsCamapaignKeywordsReportTableUtils";
import { UNDEFINED_SIGN } from "app/common/campaigns/KeywordsCampaginKeywordsTableConsts";
import type { ISelectedSites } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/KeywordsReportTypes";
import { getTableColumnsToSave } from "app/pages/digital-marketing/keywords-campaigns/reports/shared/RankTrackerReportsSharedUtils/ReportsSharedUtils";
import {
    DAY_WITHOUT_DATA,
    NOT_RANK_POSITION,
    NOT_RANKED_SIGN,
} from "app/pages/digital-marketing/keywords-campaigns/reports/shared/RTSharedConsts";
import type {
    ILanguageLocaleAndDescription,
    IRankTrackerColumn,
    IRankTrackerColumnToStore,
} from "app/pages/digital-marketing/keywords-campaigns/reports/shared/Table/TableUtils/RankTrackerTablesTypes";
import type { ISupportedLanguagesAndLocale } from "app/pages/digital-marketing/keywords-campaigns/wizard/campaigns-wizard/types";
import { PreferencesService } from "app/services/preferences/preferencesService";
import { allTrackers } from "app/services/track/track";

import type { AntColumnType } from "../../../keywords-report/keywords-table/keywordsReportTableContext";

export const getAvailableSitesToSelect = (currentCampaignConfiguration: IRTCurrentCampaignData): ISelectedSites[] => {
    const siteToIterate = currentCampaignConfiguration
        ? [
              {
                  site: currentCampaignConfiguration.site,
                  asset: currentCampaignConfiguration.asset,
                  color: currentCampaignConfiguration.color,
              },
              ...(currentCampaignConfiguration?.competitors ?? []),
          ]
        : [];
    const availableSites: ISelectedSites[] = siteToIterate.map((competitor) => {
        return {
            id: competitor.site,
            data: {
                type: "website",
                mainItem: competitor.asset,
            },
            isPrimary: false,
            updatedTime: dayjs().utc().toString(),
            isFavorite: false,
            color: competitor.color,
        };
    });
    return availableSites;
};

const getShorLanguageFromLocale = (
    scrapingConfig: ICampaignsScrapingConfiguration,
    languagesList: ISupportedLanguagesAndLocale[],
): ISupportedLanguagesAndLocale => {
    const language: ISupportedLanguagesAndLocale = languagesList?.find(
        (lang) => lang?.locale === scrapingConfig?.locale,
    );
    const descriptionToPresent = language?.description.includes(" - ")
        ? language?.description.split(" - ")[1]
        : language?.description;
    return { description: descriptionToPresent, locale: language?.locale };
};

export const getLocationCellDetails = (
    geoLocation: string,
    scrapingConfigurationId: string,
    scrapingConfigurations: ICampaignsScrapingConfiguration[],
    languagesList: ISupportedLanguagesAndLocale[],
): ILanguageLocaleAndDescription => {
    const countryCode = getCountryFromScrapingConfigId(scrapingConfigurations, scrapingConfigurationId);
    const currentScrapingConfig = scrapingConfigurations?.find(
        (scrapingConf) => scrapingConf.id === scrapingConfigurationId,
    );
    const location = geoLocation;
    const language = getShorLanguageFromLocale(currentScrapingConfig, languagesList);
    const text = `${location} (${language.description})`;
    return {
        countryCode,
        text,
        locale: language?.locale,
    };
};

export const getSearchEngineByScrapingConfigId = (
    scrapingConfigurations: ICampaignsScrapingConfiguration[],
    scrapingConfigurationId: string,
): string => {
    return scrapingConfigurations.find((scrapingConf) => scrapingConf.id === scrapingConfigurationId)?.searchEngine;
};

export const filterAndSortColumnsForTable = (
    columns: ISWReactTableWrapperWithServerDataProps["tableColumns"],
    savedStateColumns: ISWReactTableWrapperWithServerDataProps["tableColumns"],
) => {
    return columns
        .filter((col) => col)
        .sort((a, b) => {
            if (!savedStateColumns) {
                return 0;
            }
            const aIndex = savedStateColumns.findIndex(
                (col) => (!col.isGroup && col.field === a.field) || (col.isGroup && col.groupName === a.groupName),
            );
            const bIndex = savedStateColumns.findIndex(
                (col) => (!col.isGroup && col.field === b.field) || (col.isGroup && col.groupName === b.groupName),
            );
            if (aIndex === -1 || bIndex === -1) {
                return 0;
            }
            return bIndex > aIndex ? -1 : 1;
        });
};
export const getManagedColumnsConfigForTable = (
    columns: ISWReactTableWrapperWithServerDataProps["tableColumns"],
    savedStateColumns: ISWReactTableWrapperWithServerDataProps["tableColumns"],
    sortedColumn: string,
    sortDirection: ISortDirection,
): IRankTrackerColumn[] => {
    const returnedColumns = columns.map((col) => {
        const visibleStatusFromSavedState = savedStateColumns?.find(
            (savedColumns) =>
                savedColumns.field === col.field || (savedColumns.isGroup && col.groupName === savedColumns.groupName),
        )?.visible;
        const visible = visibleStatusFromSavedState ?? col.visible ?? true;
        const isSorted = sortedColumn && col.field === sortedColumn;
        return {
            ...col,
            visible,
            key: col.field,
            presentDelimiterBelow: col.presentDelimiterBelow,
            sortable: col.sortable || false,
            headerComponent: col.headerComponent || DefaultCellHeader,
            isSorted,
            sortDirection: isSorted ? sortDirection : col.sortDirection,
            isResizable: col.isResizable !== false,
        };
    });
    return returnedColumns;
};

export const updateSelectedColumnsPreferencesAndTrack = async (
    newColumns: IRankTrackerColumn[] | AntColumnType[],
    columnsPreferencesKey: string,
) => {
    const columnsToSave: IRankTrackerColumnToStore[] = getTableColumnsToSave(newColumns);
    allTrackers.dispatchTrackEvent("Button", "submit-ok", `Table/Column Settings/${newColumns.length.toString()}`);
    try {
        const filteredColumns = columnsToSave.reduce((acc, col) => {
            if (!col.isGroup || acc.every((c) => c.groupName !== col.groupName)) {
                acc.push(col);
            }
            return acc;
        }, []);
        await PreferencesService.add({
            [columnsPreferencesKey]: filteredColumns,
        });
    } catch (error) {
        Sentry.error(`Error saving columns preferences`, error);
    }
};

export const getValidPositionValue = (
    position: number | string,
    defaultValue: string | number = position,
): string | number => {
    if (position === DAY_WITHOUT_DATA) {
        return UNDEFINED_SIGN;
    }
    if (Number(position) >= NOT_RANK_POSITION) {
        return NOT_RANKED_SIGN;
    }
    return defaultValue;
};
